import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Banknote, Bell, AlertCircle, CreditCard, Receipt, Pencil, ChevronLeft } from 'lucide-react';
import AppleLogo from '../icons/AppleLogo';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * HARD-BLOCKER "Pay at the Bar" modal shown after a cash order.
 *
 * Behaviour (per user feedback IMG_2598 — customers wanted an escape hatch
 * other than walking to the bar OR ringing the bell):
 *  - Customer CANNOT close the modal by tapping outside / Esc / X.
 *  - Customer CAN change their mind on the payment method (Card / Apple Pay
 *    / Tab) — taps "Change payment method" → picks one → backend call to
 *    /orders/{id}/change-payment-method → for card/apple_pay we immediately
 *    request a Square checkout link.
 *  - Customer CAN edit the order items via the `onEdit` callback — parent
 *    opens the existing OrderEditDialog/customer-edit flow.
 *  - Customer CAN ring the Service Bell to summon staff.
 *
 * The modal stays mounted until the backend confirms the order's
 * `payment_status === 'paid'`, the order is voided / cancelled, OR the
 * customer successfully switches to a non-cash method (parent clears via
 * `alert` prop on next poll).
 *
 * Props:
 *   alert: { orderId, name, total, payment_method? } | null
 *   onRingBell: () => void
 *   onEdit: (orderId) => void   — optional; opens the customer order-edit dialog
 *   guestId: string             — required by /change-payment-method ownership check
 *   onMethodChanged: () => void — called after a successful method switch
 *                                  (so the parent re-polls + clears alert)
 */
export default function CashCounterModal({ alert, onRingBell, onEdit, guestId, onMethodChanged }) {
  const [view, setView] = useState('default');     // 'default' | 'change'
  const [working, setWorking] = useState(false);

  // Reset internal view whenever the dialog opens for a new order.
  React.useEffect(() => {
    if (alert?.orderId) setView('default');
  }, [alert?.orderId]);

  const switchMethod = async (method) => {
    if (!alert?.orderId) return;
    try {
      setWorking(true);
      await api.post(`/orders/${alert.orderId}/change-payment-method`, {
        payment_method: method,
        guest_id: guestId,
      });
      if (method === 'card' || method === 'apple_pay') {
        // Immediately spin up a Square checkout so the customer doesn't
        // bounce back into the blocker. Apple Pay surfaces on iOS Safari
        // through the same hosted checkout page.
        const { data: checkout } = await api.post('/payments/create-checkout', {
          order_id: alert.orderId,
          origin_url: window.location.origin,
          tip_amount: 0,
        });
        window.location.href = checkout.url;
        return;
      }
      // Tab method — parent should refresh orders so this blocker disappears.
      toast.success('Switched to Tab — your order is on your tab now');
      if (onMethodChanged) onMethodChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change payment method');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog
      open={!!alert}
      onOpenChange={() => {}}
    >
      <DialogContent
        className="bg-bg-primary border-2 border-amber-500/70 max-w-sm shadow-[0_0_40px_rgba(245,158,11,0.45)] [&>button.absolute]:hidden"
        data-testid="cash-counter-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {view === 'default' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-white text-center text-xl sm:text-2xl">
                <Banknote className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                Please Proceed To The Bar To Process Your Order!
              </DialogTitle>
            </DialogHeader>
            <div className="text-center pt-2 space-y-4">
              <p className="text-zinc-300 font-sans text-sm leading-relaxed">
                {alert?.name ? `Thanks ${(alert.name || '').split(' ')[0]}! ` : ''}
                Amount due
                <span className="block mt-2 text-3xl font-mono text-gold font-semibold" data-testid="cash-counter-amount">
                  ${(alert?.total || 0).toFixed(2)}
                </span>
              </p>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3">
                <p className="text-[11px] uppercase tracking-[0.2em] text-amber-400 font-sans mb-1">Order reference</p>
                <p className="font-mono text-base text-white">#{(alert?.orderId || '').slice(0, 8).toUpperCase()}</p>
              </div>

              <div className="flex items-start gap-2 text-left bg-amber-500/5 border border-amber-500/20 rounded-sm p-3">
                <AlertCircle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/90 font-sans leading-relaxed">
                  This message clears once staff confirms your payment. If cash isn&apos;t convenient,
                  pick another method or edit your order below.
                </p>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() => setView('change')}
                  className="w-full bg-white/5 border border-white/20 text-white hover:bg-white/10 rounded-sm font-sans"
                  data-testid="cash-counter-change-method"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Change payment method
                </Button>
                {onEdit && (
                  <Button
                    onClick={() => onEdit(alert?.orderId)}
                    className="w-full bg-white/5 border border-white/20 text-white hover:bg-white/10 rounded-sm font-sans"
                    data-testid="cash-counter-edit-order"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit order
                  </Button>
                )}
                {onRingBell && (
                  <Button
                    onClick={onRingBell}
                    variant="outline"
                    className="w-full bg-amber-500/15 border border-amber-500/60 text-amber-200 hover:bg-amber-500/25 rounded-sm font-sans shadow-[0_0_18px_rgba(245,158,11,0.55)] animate-[majnu-bell-pulse_1.4s_ease-in-out_infinite]"
                    data-testid="cash-counter-ring-bell"
                  >
                    <Bell className="w-4 h-4 mr-2 animate-[majnu-bell-shake_1.2s_ease-in-out_infinite]" />
                    Ring service bell for help
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {view === 'change' && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-white text-center text-lg sm:text-xl">
                <CreditCard className="w-10 h-10 text-gold mx-auto mb-2" />
                Choose a new payment method
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-1" data-testid="cash-counter-change-view">
              <p className="text-center text-xs text-zinc-400 font-sans">
                Amount due
                <span className="block mt-1 text-2xl font-mono text-gold font-semibold">
                  ${(alert?.total || 0).toFixed(2)}
                </span>
                <span className="block mt-1 text-[10px] text-amber-300/80 uppercase tracking-[0.1em]">
                  Note: 10% cash discount won&apos;t apply if you switch off cash.
                </span>
              </p>

              <div className="grid grid-cols-1 gap-2">
                <Button
                  onClick={() => switchMethod('apple_pay')}
                  disabled={working}
                  className="h-12 bg-black text-white border border-white hover:bg-white/10 rounded-sm font-sans font-semibold shadow-lg shadow-black/40 disabled:opacity-50"
                  data-testid="cash-counter-switch-apple_pay"
                >
                  <AppleLogo className="w-4 h-4 mr-2" />
                  Apple Pay · ${(alert?.total || 0).toFixed(2)}
                </Button>
                <Button
                  onClick={() => switchMethod('card')}
                  disabled={working}
                  className="h-12 bg-blue-accent/15 border border-blue-accent/50 text-blue-accent hover:bg-blue-accent/25 rounded-sm font-sans font-semibold disabled:opacity-50"
                  data-testid="cash-counter-switch-card"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  EFTPOS · ${(alert?.total || 0).toFixed(2)}
                </Button>
                <Button
                  onClick={() => switchMethod('tab')}
                  disabled={working}
                  className="h-12 bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20 rounded-sm font-sans font-semibold disabled:opacity-50"
                  data-testid="cash-counter-switch-tab"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Move to Tab (if open)
                </Button>
              </div>

              <Button
                onClick={() => setView('default')}
                disabled={working}
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white font-sans text-xs"
                data-testid="cash-counter-back"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
